import { Link } from 'react-router-dom';
import type { UsageResponse } from '../types';

interface LimitWarningProps {
  usage: UsageResponse | undefined;
  /** Which limit to check. Issuers/badges are absolute counts; awards is yearly. */
  resource: 'issuers' | 'badges' | 'awards';
}

function formatResetDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

/**
 * Renders a warning banner when the user is at or above the plan limit
 * for the given resource. Returns null otherwise.
 *
 * Distinguishes yearly limits (awards) from absolute counts (issuers,
 * badges) — the yearly variant tells the user when the counter resets.
 */
export function LimitWarning({ usage, resource }: LimitWarningProps) {
  if (!usage) return null;

  const { limits, usage: u, yearResetAt, plan } = usage;

  // Resource-specific limit + used.
  let limit: number | null;
  let used: number;
  let yearly = false;
  let label: string;

  if (resource === 'issuers') {
    limit = limits.issuers; used = u.issuers; label = 'issuer';
  } else if (resource === 'badges') {
    limit = limits.badges; used = u.badges; label = 'badge';
  } else {
    limit = limits.awardsPerYear; used = u.awardsThisYear; yearly = true; label = 'award';
  }

  // null limit = unlimited (Pro plan for issuers/badges).
  if (limit == null) return null;
  if (used < limit) return null;

  const isUnlimitedAvailable = plan !== 'top';

  return (
    <div
      data-testid={`limit-warning-${resource}`}
      data-resource={resource}
      data-yearly={yearly ? 'true' : 'false'}
      role="alert"
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
    >
      <p className="font-semibold">
        You've reached the {label} limit on your {plan} plan ({used} / {limit}).
      </p>
      {yearly ? (
        <p className="mt-1 text-amber-800">
          Your yearly credential pool resets on <strong>{formatResetDate(yearResetAt)}</strong>.
          {isUnlimitedAvailable && <> Upgrade your plan for a higher yearly allowance.</>}
        </p>
      ) : (
        <p className="mt-1 text-amber-800">
          Delete an existing {label}
          {isUnlimitedAvailable && <> or upgrade your plan</>} to create more.
        </p>
      )}
      {isUnlimitedAvailable && (
        <Link
          to="/pricing"
          className="mt-2 inline-block rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
        >
          View plans
        </Link>
      )}
    </div>
  );
}

/**
 * Returns true if the user is at or above the plan limit for the given resource.
 * Used to disable "New" buttons.
 */
export function isAtLimit(usage: UsageResponse | undefined, resource: 'issuers' | 'badges' | 'awards'): boolean {
  if (!usage) return false;
  const { limits, usage: u } = usage;
  let limit: number | null;
  let used: number;
  if (resource === 'issuers') { limit = limits.issuers; used = u.issuers; }
  else if (resource === 'badges') { limit = limits.badges; used = u.badges; }
  else { limit = limits.awardsPerYear; used = u.awardsThisYear; }
  if (limit == null) return false;
  return used >= limit;
}
